// The PIN screen (2026-09-28, the user's brief: "a cool pin design with a
// container for every number, the logo above it and a styled message
// under it"; REDESIGNED 2026-10-05 from the user's reference screen): on
// the dark SLATE ground, the DOT LOGO (assets/Logo_Dots.png) over FOUR
// flat grey boxes, then "Enter your access code" over a quieter line —
// sizes and colours measured off the reference (index.css, THE WALL). Codes are
// four digits (apps/api/scripts/assign_brand_auth_codes.py issues four;
// the column allows up to ten — a longer code is a future change here).
//
// Mechanics: one input per box, numeric keypad on phones, autocomplete
// hinted as a one-time code; a digit advances the focus, Backspace on an
// empty box steps back, a paste fills from the first box, and the FOURTH
// digit SUBMITS — no button. A wrong code shakes the row, clears it and
// puts the caret back in the first box; a throttled IP (429) reads its
// wait; a code whose brand has no authored workspace
// (contexts/SessionContext) says so and keeps the digits. The message
// under the boxes is the screen's h1.
//
// THE WAIT (2026-10-01, user call: "best practice, optimal, premium
// loader — the loading state is not handled"; the row only dimmed until
// then). The loader is a SPINNER (the same day, user call: "why don't we
// show some spinner … or something premium" — a lap of the focus ring
// along the boxes, the first build, did not read as loading): a small
// ring in the air between the logo and the boxes, placed off the
// masthead, so nothing enters the layout.
//   checking  the ring TURNS under the logo (components/Ring, the app's one
//             spinner — the app loader's too since 2026-10-15; `.gate-ring`), the
//             boxes lock with their digits at full ink, the title reads
//             "Checking your code". No show-delay: the server paces every
//             answer to 0.3s at least (apps/api modules/auth), so the
//             state can never flash.
//   slow      past SLOW_MS (a cold API instance) the line under the title
//             says it is still checking; the request itself gives up at
//             services/authService AUTH_TIMEOUT_MS and reads UNREACHABLE.
//   accepted  the ring CLOSES into a full circle, the row turns the logo's green
//             and lands, the title reads "You're in" over the workspace's
//             name, held WELCOME_MS — then the
//             gate opens inside a view transition (the session is stored
//             the moment the code is accepted: `admit`, then `enter`).
// A screen reader hears the same from an sr-only status line; the notice
// stays the alert.
//
// `linkCode` (components/AuthGate): a code that arrived in the URL. It is
// spent ONCE, on mount — the boxes fill with it and it submits as if typed,
// so a shared link opens the workspace with the same feedback a hand-typed
// code gets (the dimmed row, then the app; or the shake and the notice).
// An empty string means the URL carried a malformed code: the wall says so
// and waits for a hand-typed one.
import { useCallback, useEffect, useRef, useState } from "react";

import logoDots from "assets/Logo_Dots.png";
import Ring from "components/Ring";
import { useSession } from "contexts/SessionContext";
import { GROUND_SLATE, useGround } from "hooks/useGround";
import { withViewTransition } from "utils/viewTransition";

export const CODE_LENGTH = 4;

// the copy under the boxes: a title line and a quieter line, in the Lab's
// register (user call 2026-09-28: "more premium")
export const TITLE = "Enter your access code";
export const SUBTITLE = "Your four-digit code opens your brand's workspace.";

// the wait, in the same voice
export const CHECKING = "Checking your code";
export const CHECKING_SUB = "One moment.";
export const SLOW_SUB = "Taking longer than usual — still checking.";
export const ACCEPTED = "You're in";
export const openingLine = (name) => `Opening the ${name} workspace.`;

// when the wait admits it is long, and how long the welcome is held
export const SLOW_MS = 4000;
export const WELCOME_MS = 700;

// the notices — plain sentences, never a system voice
export const WRONG_CODE =
  "That code doesn't open a workspace. Check it and try again.";
export const THROTTLED = "Too many attempts. Give it a minute, then try again.";
export const UNREACHABLE =
  "We can't reach the index right now. Try again shortly.";
export const BAD_LINK =
  "That link doesn't carry a valid code. Enter yours below.";

const EMPTY = () => Array(CODE_LENGTH).fill("");
const digitsOf = (text) => (text || "").replace(/\D/g, "");

export default function Login({ linkCode = null }) {
  const { admit, enter } = useSession();
  useGround(GROUND_SLATE);
  // a link's code shows in the boxes from the first frame, already checking
  const [digits, setDigits] = useState(() =>
    linkCode
      ? Array.from({ length: CODE_LENGTH }, (_, i) => linkCode[i] || "")
      : EMPTY(),
  );
  const [busy, setBusy] = useState(Boolean(linkCode));
  const [slow, setSlow] = useState(false);
  // the workspace the code opened — set for the welcome beat only
  const [opened, setOpened] = useState(null);
  const [error, setError] = useState(linkCode === "" ? BAD_LINK : "");
  const [shake, setShake] = useState(false);
  const inputs = useRef([]);
  const submittedRef = useRef("");
  // a box to focus once the row is enabled again (a miss clears the row
  // while the boxes are still disabled from the check — focus must wait
  // for the commit that re-enables them)
  const [refocus, setRefocus] = useState(null);

  useEffect(() => {
    document.title = "Sign in · 3.5";
    inputs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (busy || refocus == null) return;
    inputs.current[refocus]?.focus();
    setRefocus(null);
  }, [busy, refocus]);

  useEffect(() => {
    if (!busy || opened) return undefined;
    const timer = setTimeout(() => setSlow(true), SLOW_MS);
    return () => {
      clearTimeout(timer);
      setSlow(false);
    };
  }, [busy, opened]);

  // the welcome is held, then the gate opens — the wall dissolving into
  // the app, not cut to it
  useEffect(() => {
    if (!opened) return undefined;
    const timer = setTimeout(
      () => withViewTransition(() => enter(opened)),
      WELCOME_MS,
    );
    return () => clearTimeout(timer);
  }, [opened, enter]);

  const focusAt = (i) =>
    inputs.current[Math.max(0, Math.min(CODE_LENGTH - 1, i))]?.focus();

  const submit = useCallback(
    async (code, { fromLink = false } = {}) => {
      // the link's own submit is the one call allowed through the busy
      // state it set for itself
      if ((busy && !fromLink) || submittedRef.current === code) return;
      submittedRef.current = code;
      setBusy(true);
      setError("");
      try {
        // the row stays busy through the welcome — the gate swaps this
        // screen out at the end of it, nothing to reset
        setOpened(await admit(code));
      } catch (err) {
        setBusy(false);
        submittedRef.current = "";
        if (err?.noWorkspace) {
          setError(err.message);
          return;
        }
        if (err?.status === 429) {
          setError(THROTTLED);
          return;
        }
        if (err?.status === 401) {
          setError(WRONG_CODE);
        } else {
          setError(UNREACHABLE);
        }
        setShake(true);
        setDigits(EMPTY());
        setRefocus(0);
      }
    },
    [busy, admit],
  );

  // the link's code is spent once, on mount (the ref empties itself, so
  // the re-run when `submit` changes with `busy` does nothing)
  const linkRef = useRef(linkCode);
  useEffect(() => {
    const code = linkRef.current;
    linkRef.current = null;
    if (code) submit(code, { fromLink: true });
  }, [submit]);

  // a full row submits itself — from typing or from a paste
  const commit = (next, from) => {
    setDigits(next);
    const code = next.join("");
    if (code.length === CODE_LENGTH && next.every(Boolean)) {
      submit(code);
    } else {
      focusAt(from);
    }
  };

  const onChange = (i) => (e) => {
    const typed = digitsOf(e.target.value);
    if (!typed) {
      const next = [...digits];
      next[i] = "";
      setDigits(next);
      return;
    }
    // a paste (or a keypad's autofill) lands as several digits in one box
    const next = [...digits];
    let at = i;
    for (const d of typed) {
      if (at >= CODE_LENGTH) break;
      next[at] = d;
      at += 1;
    }
    setError("");
    commit(next, at);
  };

  const onKeyDown = (i) => (e) => {
    if (e.key === "Backspace") {
      if (digits[i]) {
        const next = [...digits];
        next[i] = "";
        setDigits(next);
      } else if (i > 0) {
        e.preventDefault();
        const next = [...digits];
        next[i - 1] = "";
        setDigits(next);
        focusAt(i - 1);
      }
      return;
    }
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusAt(i - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusAt(i + 1);
    }
  };

  const onPaste = (i) => (e) => {
    const text = digitsOf(e.clipboardData?.getData("text"));
    if (!text) return;
    e.preventDefault();
    const next = [...digits];
    let at = i;
    for (const d of text) {
      if (at >= CODE_LENGTH) break;
      next[at] = d;
      at += 1;
    }
    setError("");
    commit(next, at);
  };

  const title = opened ? ACCEPTED : busy ? CHECKING : TITLE;
  const sub = opened
    ? openingLine(opened.name)
    : busy
      ? slow
        ? SLOW_SUB
        : CHECKING_SUB
      : SUBTITLE;
  const state = opened ? " open" : busy ? " busy" : "";

  return (
    <main className="gate page-in">
      <span className={`gate-masthead${state}`}>
        {/* the file is 573 × 228 — the attributes hold its box before it loads */}
        <img
          className="gate-logo"
          src={logoDots}
          alt="Cultural IQ"
          width="573"
          height="228"
        />
        {/* the spinner: the app's ring (components/Ring), turning under
            the logo — the masthead's `busy` / `open` drive it */}
        <Ring className="gate-ring" />
      </span>
      <form
        className={`pin${shake ? " shake" : ""}${state}`}
        aria-label="Workspace code"
        aria-busy={busy || undefined}
        onAnimationEnd={() => setShake(false)}
        onSubmit={(e) => e.preventDefault()}
      >
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => {
              inputs.current[i] = el;
            }}
            className={`pin-box${d ? " filled" : ""}`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            maxLength={CODE_LENGTH}
            aria-label={`Digit ${i + 1} of ${CODE_LENGTH}`}
            aria-invalid={error === WRONG_CODE || undefined}
            value={d}
            disabled={busy}
            onChange={onChange(i)}
            onKeyDown={onKeyDown(i)}
            onPaste={onPaste(i)}
            onFocus={(e) => e.target.select()}
          />
        ))}
      </form>
      {/* keyed by their text: a new line fades in where the old one stood */}
      <h1 className="gate-msg gate-swap" key={title}>
        {title}
      </h1>
      <p className="gate-sub gate-swap" key={sub}>
        {sub}
      </p>
      <p className="sr-only" role="status">
        {opened
          ? `Code accepted. ${openingLine(opened.name)}`
          : busy
            ? CHECKING
            : ""}
      </p>
      {/* the notice keeps its slot empty so the copy never jumps; a notice
          is a frosted chip — a small mark, the sentence — that rises in */}
      <div className="gate-err" role="alert" aria-live="polite">
        {error && (
          <span className="gate-err-chip" key={error}>
            <svg
              className="gate-err-mark"
              viewBox="0 0 20 20"
              width="18"
              height="18"
              aria-hidden="true"
            >
              <circle cx="10" cy="10" r="9" fill="currentColor" />
              <path
                d="M10 5.5v5.2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle cx="10" cy="14.2" r="1.15" />
            </svg>
            {error}
          </span>
        )}
      </div>
    </main>
  );
}
